import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { Card, Snackbar, Alert } from '@mui/material'; // Added Snackbar, Alert
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { OrgContext } from 'context/orgContext';
import StageEnvironment from './components/StageEnvironment';
import StageCommit from './components/StageCommit';
import StageReview from './components/StageReview';
import StageProduct from './components/StageProduct';
import { ProductContext } from 'context/productContext';
import { UserContext } from 'context/userContext';
import { userOrgs } from 'services/org';
import { getSalesforceProducts, getMajorVersionsFromDb, getProductsFromDb } from 'services/product';
import { useNotifications } from 'context/NotificationContext'; // Import notification hook

function Commit() {
  const [activeStage, setActiveStage] = useState('environment');
  const [renderStages, setRender] = useState(false); // Initialize to false
  const { userId } = useContext(UserContext);
  const { orgs, setOrgs } = useContext(OrgContext);
  const { products, setProducts } = useContext(ProductContext);
  const isInitialMount = useRef(true);
  const [localUserId, setLocalUserId] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [removedProducts, setRemovedProducts] = useState([]);
  const [productFields, setProductFields] = useState([]);
  const [modifiedProducts, setModifiedProducts] = useState([]);
  const [commitProducts, setCommitProducts] = useState([]);
  const [versionType, setVersionType] = useState('');
  const [sfProducts, setSfProducts] = useState([]);
  const [majorVersionProducts, setMajorVersionProducts] = useState([]);
  const [finalSelectedMajorVersions, setFinalSelectedMajorVersions] = useState([]);
  const [parentDatabaseProducts, setDatabaseProducts] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [isRevenue, setIsRevenue] = useState('');
  const [isReviewCompleted, setIsReviewCompleted] = useState(false);

  // Snackbar and Notification states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const { addNotification } = useNotifications();

  const handleMajorVersionsSelected = useCallback((selectedVersions) => {
    console.log('[handleMajorVersionsSelected] selectedVersions', selectedVersions);
    setFinalSelectedMajorVersions(selectedVersions);
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleVersionTypeChange = (newVersionType) => {
    if (newVersionType !== 'patch') {
      setMajorVersionProducts([]);
      setFinalSelectedMajorVersions([]);
    } else {
      setDatabaseProducts([]);
    }
    setVersionType(newVersionType);
  };

  const handleStageChange = (stage) => {
    if (stage !== 'environment' && (!selectedOrg || !versionType)) {
      const message = 'Please select an environment and version type before proceeding.';
      addNotification({ title: ['Warning', 'Selection Required'], severity: 'warning', details: message });
      showSnackbar(message, 'warning');
      return;
    }

    if (stage === 'review' && selectedProducts.length === 0) {
      const message = 'You must select at least one product before proceeding to the review stage.';
      addNotification({ title: ['Action Required'], severity: 'warning', details: message });
      showSnackbar(message, 'warning');
      return; // Block navigation
    }

    if (stage === 'commit' && !isReviewCompleted) {
      const message = 'You must complete the "Review Changes" stage before committing.';
      addNotification({ title: ['Action Required'], severity: 'warning', details: message });
      showSnackbar(message, 'warning');
      return; // Block navigation to the commit stage
    }

    setActiveStage(stage);
  };

  const selectedEnv = (orgObject) => {
    if (!orgObject) {
      setSelectedOrg(null);
      setVersionType('');
      setMajorVersionProducts([]);
      setFinalSelectedMajorVersions([]);
      setSfProducts([]);
      setProductFields([]);
      addNotification({ title: ['Info', 'Environment Cleared'], severity: 'info' });
      return;
    }

    const selected = orgObject;

    console.log('[selectedEnv] - selected', selected);

    setIsRevenue(selected.type);
    setSelectedOrg(selected);
    addNotification({
      title: ['Info', 'Environment Selected'],
      severity: 'info',
      details: `Selected environment: ${selected.name}`,
    });
  };

  useEffect(() => {
    setLocalUserId(userId);
  }, [userId]);

  useEffect(() => {
    const fetchOrgs = async () => {
      if (!userId) return; // Don't fetch if userId isn't set from context
      try {
        const { orgs: fetchedOrgs } = await userOrgs();
        if (fetchedOrgs) {
          setOrgs(fetchedOrgs);
        }
      } catch (error) {
        const errorMsg = `Error fetching organizations: ${error.message}`;
        addNotification({ title: ['Error', 'Org Fetch Error'], severity: 'error', details: errorMsg });
        showSnackbar(errorMsg, 'error');
      } finally {
        setRender(true); // Allow rendering stages now
      }
    };

    if (!orgs || orgs.length === 0) {
      fetchOrgs();
    } else {
      setRender(true); // Orgs are already in context, just render.
    }
  }, [userId, orgs, setOrgs, addNotification]);

  useEffect(() => {
    // This effect runs whenever the list of selected products changes.
    if (selectedProducts.length === 0) {
      // If the user has cleared their selections, they are no longer
      // considered to have completed the review stage.
      setIsReviewCompleted(false);
    }
  }, [selectedProducts]);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedOrg || !selectedOrg.org_id || !localUserId || !versionType) {
        // Ensure all dependencies are ready
        setSfProducts([]);
        setMajorVersionProducts([]);
        setDatabaseProducts([]);
        return;
      }

      console.log('[fetchData] - selectedOrg', selectedOrg);

      try {
        if (versionType === 'patch') {
          const majorVersions = await getMajorVersionsFromDb(selectedOrg.org_id, []);
          const fetchedMajorVersions = majorVersions?.products || [];
          if (majorVersions?.error || !majorVersions?.products) {
            addNotification({
              title: ['Warning', 'Major Versions Fetch Failed'],
              severity: 'warning',
              details: majorVersions?.message || 'Could not fetch major versions.',
            });
          }

          const sfProductsResponse = await getSalesforceProducts(selectedOrg.org_id);
          const fetchedSfProducts = sfProductsResponse?.products || [];
          if (sfProductsResponse?.error || !sfProductsResponse?.products) {
            addNotification({
              title: ['Warning', 'Salesforce Products Fetch Failed'],
              severity: 'warning',
              details: sfProductsResponse?.message || 'Could not fetch Salesforce products.',
            });
          }

          const intersectedMajorVersions = fetchedMajorVersions.filter((major) =>
            fetchedSfProducts.some(
              (sfProduct) =>
                (major.external_id && sfProduct.external_id && major.external_id === sfProduct.external_id) ||
                (major.sf_id && sfProduct.sfId && major.sf_id === sfProduct.sfId) ||
                (major.id && sfProduct.id && major.id === sfProduct.id)
            )
          );

          const filteredSfProducts = fetchedSfProducts.filter((sfProduct) =>
            intersectedMajorVersions.some(
              (major) =>
                (major.external_id && sfProduct.external_id && major.external_id === sfProduct.external_id) ||
                (major.sf_id && sfProduct.sfId && major.sf_id === sfProduct.sfId) ||
                (major.id && sfProduct.id && major.id === sfProduct.id)
            )
          );

          setMajorVersionProducts(intersectedMajorVersions);
          setSfProducts(filteredSfProducts);
          if (intersectedMajorVersions.length === 0 || filteredSfProducts.length === 0) {
            addNotification({
              title: ['Info', 'No Matching Products'],
              severity: 'info',
              details: 'No products found matching between Salesforce and existing major versions for patching.',
            });
          }
        } else {
          // For 'new' version type
          const sfProductsResponse = await getSalesforceProducts(selectedOrg.org_id);
          const fetchedSfProducts = sfProductsResponse?.products || [];
          setSfProducts(fetchedSfProducts);
          if (sfProductsResponse?.error || !sfProductsResponse?.products) {
            addNotification({
              title: ['Warning', 'Salesforce Products Fetch Failed'],
              severity: 'warning',
              details: sfProductsResponse?.message || 'Could not fetch Salesforce products.',
            });
          } else if (fetchedSfProducts.length === 0) {
            addNotification({
              title: ['Info', 'No Products Found'],
              severity: 'info',
              details: 'No products found in the selected Salesforce environment.',
            });
          }

          let db = await getProductsFromDb(selectedOrg.org_id, []);
          const fetchedDbProducts = db?.products || [];
          setDatabaseProducts(fetchedDbProducts);
          if (db?.error || !db?.products) {
            addNotification({
              title: ['Warning', 'Database Products Fetch Failed'],
              severity: 'warning',
              details: db?.message || 'Could not fetch existing database products.',
            });
          }
        }
      } catch (error) {
        const errorMsg = `Error fetching product data: ${error.message}`;
        addNotification({ title: ['Error', 'Product Fetch Error'], severity: 'error', details: errorMsg });
        showSnackbar(errorMsg, 'error');
        setSfProducts([]);
        setMajorVersionProducts([]);
        setDatabaseProducts([]);
      }
    };

    // Avoid running on initial mount before selections are made
    if (!isInitialMount.current) {
      fetchData();
    } else {
      isInitialMount.current = false;
    }
  }, [localUserId, selectedOrg, versionType, addNotification]); // Removed setProducts, isInitialMount

  useEffect(() => {
    if (activeStage === 'environment') {
      setSelectedProducts([]);
      setRemovedProducts([]);
      setFinalSelectedMajorVersions([]); // Reset major version selections too
      setModifiedProducts([]);
      setCommitProducts([]);
    }
  }, [activeStage]);

  const handleModifiedProducts = useCallback((updatedProducts) => {
    setModifiedProducts(updatedProducts);
    // If we receive valid products from the review stage, it means the stage is complete.
    if (updatedProducts && updatedProducts.length > 0) {
      setIsReviewCompleted(true);
    } else {
      // If the review results in no products to modify, it's also "complete" but there's nothing to commit.
      // We can still mark it as complete to allow the user to see the empty commit stage if we want.
      setIsReviewCompleted(true);
    }
  }, []);

  const renderStageContent = () => {
    switch (activeStage) {
      case 'environment':
        return (
          <StageEnvironment
            envs={orgs || []} // Pass orgs or empty array
            selected={selectedOrg}
            selectedEnv={selectedEnv}
            onVersionTypeChange={handleVersionTypeChange}
            initialVersionType={versionType}
          />
        );
      case 'products':
        // Ensure required props are available before rendering
        if (!selectedOrg || !versionType)
          return <ArgonTypography>Please select environment and version type first.</ArgonTypography>;
        return (
          <StageProduct
            variants={sfProducts}
            selectedVariants={setSelectedProducts}
            initialSelected={selectedProducts}
            versionType={versionType}
            majorVersionProducts={majorVersionProducts}
            onMajorVersionsSelected={handleMajorVersionsSelected}
            parentDatabaseProducts={parentDatabaseProducts}
            initialMajorVersions={finalSelectedMajorVersions}
          />
        );
      case 'review':
        if (selectedProducts.length === 0)
          return <ArgonTypography>Please select environment, version type, and products first.</ArgonTypography>;
        return (
          <StageReview
            userId={userId}
            org={selectedOrg}
            orgId={selectedOrg.org_id}
            isRevenue={isRevenue === 'RC'}
            productFields={productFields}
            selectedProducts={selectedProducts}
            onModifiedProducts={handleModifiedProducts}
            databaseMajorVersions={finalSelectedMajorVersions}
            setCommitProducts={setCommitProducts}
            versionType={versionType}
          />
        );
      case 'commit':
        if (!isReviewCompleted) {
          return <ArgonTypography>Please complete the review stage first.</ArgonTypography>;
        }
        return (
          <StageCommit
            userId={userId}
            org={selectedOrg}
            selectedProducts={modifiedProducts}
            removedProducts={removedProducts}
            commitProducts={commitProducts}
            handleStageChange={handleStageChange}
            versionType={versionType}
          />
        );
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox display="flex" justifyContent="space-between" alignItems="center" p={3} flexWrap="wrap">
              <ArgonTypography
                variant="button"
                color={activeStage === 'environment' ? 'info' : 'text'}
                fontWeight="regular"
                onClick={() => handleStageChange('environment')}
                sx={{ cursor: 'pointer' }}
              >
                Select Environment
              </ArgonTypography>
              <ArgonTypography> → </ArgonTypography>
              <ArgonTypography
                variant="button"
                color={activeStage === 'products' ? 'info' : 'text'}
                fontWeight="regular"
                onClick={() => handleStageChange('products')}
                sx={{
                  cursor: selectedOrg && versionType ? 'pointer' : 'default',
                  opacity: selectedOrg && versionType ? 1 : 0.5,
                }}
              >
                Select Products
              </ArgonTypography>
              <ArgonTypography> → </ArgonTypography>
              <ArgonTypography
                variant="button"
                color={activeStage === 'review' ? 'info' : 'text'}
                fontWeight="regular"
                onClick={() => handleStageChange('review')}
                sx={{
                  // --- VISUALLY DISABLE THE REVIEW LINK ---
                  cursor: selectedProducts.length > 0 ? 'pointer' : 'not-allowed',
                  opacity: selectedProducts.length > 0 ? 1 : 0.5,
                }}
              >
                Review Changes
              </ArgonTypography>
              <ArgonTypography> → </ArgonTypography>
              <ArgonTypography
                variant="button"
                color={activeStage === 'commit' ? 'info' : 'text'}
                fontWeight="regular"
                onClick={() => handleStageChange('commit')}
                sx={{
                  // The logic for this link is already correct
                  cursor: isReviewCompleted ? 'pointer' : 'not-allowed',
                  opacity: isReviewCompleted ? 1 : 0.5,
                }}
              >
                Commit Changes
              </ArgonTypography>
            </ArgonBox>
          </Card>
          {renderStages && renderStageContent()}
        </ArgonBox>
      </ArgonBox>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} // Changed anchor to top
      >
        <Alert
          onClose={handleSnackbarClose}
          variant="filled"
          severity={snackbarSeverity}
          sx={{ alignItems: 'center', alignContent: 'center' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default Commit;
